// ============================================================================
// BREAKABLE ENTITY (Base Class)
//
// CATEGORIZATION STRUCTURE:
// 1. Environmental Static: Pure obstacles (Walls, Bed, Desk) - Not in this base.
// 2. Active Interactive: "Rage" targets (Cupboard, Door, Wardrobe, Chest).
//    - Uses Hold-to-Charge aggression system.
//    - Takes damage based on aggression level.
//    - Spawns fragments when breakThreshold is reached.
// 3. Physical Fragments: Pickables/Throwables (DetachedDoor, MirrorPiece).
//    - Immediate pickup, charged throw.
// ============================================================================

import CONFIG from '../config.js';
import { distance } from '../utils/helpers.js';

export class BreakableEntity {
    constructor(x, y, width, height, physics = null) {
        this.x = x;
        this.y = y;
        this.width = width;
        this.height = height;
        this.physics = physics;
        
        // Interaction State
        this.isHolding = false;
        this.holdStartTime = 0;  // Track when hold started (for baseline time)
        this.holdDuration = 0;   // Track total hold duration
        this.isBroken = false;
        this.damage = 0;
        this.lastDamageAmount = 0;
        
        // Physics Body (subclasses should initialize this)
        this.body = null;
        
        // Sensor body for interaction detection (circular, positioned below entity)
        this.sensorBody = null;
        if (physics) {
            const sensorRadius = (CONFIG.interactionRadius || 50) * 0.8; // 20% smaller
            const sensorY = y + height / 2 + 10; // Position sensor below the entity
            this.sensorBody = physics.createSensor(x, sensorY, sensorRadius);
            physics.linkEntity(this, this.sensorBody);
        }
    }

    /**
     * Start the hold interaction.
     * Resets player aggression to 0 and prepares for buildup.
     */
    startHold(player, game) {
        if (!this.isBroken && this.canInteract()) {
            this.isHolding = true;
            this.holdStartTime = performance.now() / 1000; // Store start time in seconds
            this.holdDuration = 0;
            player.aggression = 0; // Reset aggression to 0 at start of hold
            
            // Start wood creak loop
            if (game && game.audio) {
                game.audio.startCreakLoop();
            }
        }
    }

    /**
     * Hook for subclasses to add conditions for interaction (e.g. AlarmClock must be ON)
     */
    canInteract() {
        return true;
    }

    /**
     * Buildup aggression while button is held.
     * Aggression starts accumulating immediately, but damage only applies after baseline time.
     */
    updateHold(dt, player, debugMode = false) {
        if (this.isHolding) {
            const currentTime = performance.now() / 1000;
            this.holdDuration = currentTime - this.holdStartTime;
            
            // Aggression starts accumulating immediately (for visual feedback)
            const multiplier = debugMode ? CONFIG.debugAggressionMultiplier : 1;
            player.aggression = Math.min(this.holdDuration * CONFIG.aggressionRate * multiplier, CONFIG.maxConMaxAggression);
        }
    }

    /**
     * Release the interaction.
     * Performs the entity's action and applies damage based on aggression level.
     */
    releaseHold(game) {
        if (!this.isHolding || this.isBroken) return;

        this.isHolding = false;
        const aggression = game.player.aggression;

        // 1. Determine aggression level (0-3)
        let level = 0;
        if (aggression >= CONFIG.aggressiveMax) level = 3;
        else if (aggression >= CONFIG.moderateMax) level = 2;
        else if (aggression >= CONFIG.gentleMax) level = 1;

        // 2. Perform subclass-specific action (open door, shake tree, etc.)
        this.performAction(level, aggression, game);

        // 3. Universal feedback (Shake, Particles, Sound)
        game.camera.shake(CONFIG.shakeAmplitudes[level], CONFIG.shakeDurations[level]);
        this.emitParticles(game, level, aggression);
        
        // Stop creak loop
        if (game.audio) {
            game.audio.stopCreakLoop();
        }
        
        // Play slam sound based on aggression
        if (game.audio) {
            game.audio.playSlam(aggression, CONFIG.maxConMaxAggression);
        }

        // 4. Special visual for max aggression
        if (level === 3) {
            game.flashAlpha = 0.7;
            game.camera.setZoom(CONFIG.extremeZoom, CONFIG.extremeZoomDuration);
            setTimeout(() => {
                game.camera.setZoom(1.0, CONFIG.extremeZoomDuration);
            }, CONFIG.extremeZoomDuration * 1000);
        }

        // 5. Apply Damage
        this.applyDamage(aggression, game);
        
        // Track last interaction aggression for debug display
        if (game) {
            game.lastInteractionAggression = aggression;
        }

        // 6. Cleanup
        game.player.aggression = 0;
    }

    /**
     * Apply damage to the entity based on aggression.
     * Only applies damage if the interaction lasted longer than baseline time.
     * Damage is based on aggression accumulated beyond the baseline time.
     */
    applyDamage(aggression, game) {
        // Only apply damage if hold duration exceeded baseline time
        if (this.holdDuration > CONFIG.interactionBaselineTime) {
            // Damage equals the full aggression value at release time
            // Example: if released at 702ms with aggression 3, damage = 3 (not 3 - baseline)
            this.lastDamageAmount = aggression;
            this.damage += aggression;

            if (this.damage >= this.getBreakThreshold() && !this.isBroken) {
                this.breakApart(game);
            }
        } else {
            // No damage if released before baseline time (700ms)
            this.lastDamageAmount = 0;
        }
    }

    /**
     * Placeholder for the subclass action.
     */
    performAction(level, aggression, game) {
        // Subclasses implement this (e.g. this.isOpen = !this.isOpen)
    }

    /**
     * Hook for subclasses to define their specific break threshold.
     */
    getBreakThreshold() {
        return CONFIG.breakThreshold;
    }

    /**
     * Handle destruction of the entity.
     */
    breakApart(game) {
        this.isBroken = true;
        this.isHolding = false; // Stop holding when broken
        
        // Stop creak loop if it was playing
        if (game.audio) {
            game.audio.stopCreakLoop();
        }
        
        // Remove physics body
        if (this.body && this.physics) {
            this.physics.removeBody(this.body);
            this.body = null;
        }

        // Trigger subclass-specific fragment spawning
        this.onBreak(game);

        // Universal break feedback
        game.camera.shake(15, 0.8);
        game.flashAlpha = 0.9;
    }

    onBreak(game) {
        // Subclasses spawn their fragments here
    }

    emitParticles(game, level, aggression) {
        game.particles.emit(this.x, this.y, CONFIG.particleCounts[level], aggression / 2);
    }

    isNearPlayer(player) {
        if (!player || !player.body || !this.sensorBody || !this.physics) return false;
        // Use physics sensor to detect if player is in range
        return this.physics.isPlayerInSensor(this.sensorBody, player.body);
    }

    /**
     * Draws UI elements when holding (aggression bar above the entity).
     */
    drawUI(ctx, camera, player) {
        // Only show aggression bar when holding
        if (!this.isHolding || !player) {
            return;
        }
        
        // Get the actual entity position (from physics body if available, otherwise use stored position)
        // This should match what entities use in their draw() methods
        let entityX = this.x;
        let entityY = this.y;
        
        // If entity has a physics body, use its position (more accurate)
        if (this.body && this.body.position) {
            entityX = this.body.position.x;
            entityY = this.body.position.y;
        }
        
        // NOTE: Camera transform is already applied in game.js before calling drawUI
        // So we use world coordinates directly - they will be transformed by the existing camera transform
        
        // Position bar above the entity (20% bigger, 10px above)
        // For 3/4 perspective, entities have heightOffset, so we need to account for visual top
        // Most entities have heightOffset around 20-40px, so we'll estimate the visual top
        const estimatedHeightOffset = 30; // Average heightOffset for most entities
        const barWidth = 60; // Keep width same
        const barHeight = 10; // Make thicker (was 7)
        
        // Calculate bar position in world coordinates (camera transform already applied)
        const barX = entityX - barWidth / 2;
        // Position 10px above the visual top of the entity
        const visualTopY = entityY - this.height / 2 - estimatedHeightOffset;
        const barY = visualTopY - barHeight - 10; // 10px above the visual top
        const cornerRadius = 3; // For rounded corners
        
        const maxAggression = CONFIG.maxConMaxAggression;
        const currentAggression = Math.max(0, Math.min(player.aggression || 0, maxAggression));
        const fillPercent = Math.max(0, Math.min(1, currentAggression / maxAggression)); // Clamp 0-1
        
        // Determine bar color based on hold duration and aggression level
        let barColor;
        let barBgColor; // Lighter/transparent version for background
        const baselineTime = CONFIG.interactionBaselineTime; // 1000ms (1.0 seconds)
        const slamHighThreshold = maxAggression * 0.5; // 50% of max (50 aggression points)
        
        // Color logic (must check in order):
        // 1. Green: holdDuration < 1000ms (no damage period, aggression can still accumulate for visual feedback)
        // 2. Red: currentAggression >= 50% of max (slam-highest threshold)
        // 3. Yellow: holdDuration >= 1000ms AND currentAggression < 50% of max (slam-high range)
        if (this.holdDuration < baselineTime) {
            // Green: before 1000ms (no damage period) - bar fills smoothly from 0%
            barColor = '#4CAF50';
            barBgColor = 'rgba(76, 175, 80, 0.3)'; // Lighter/transparent green
        } else if (currentAggression >= slamHighThreshold) {
            // Red: at or above 50% of max aggression (slam-highest)
            barColor = '#F44336';
            barBgColor = 'rgba(244, 67, 54, 0.3)'; // Lighter/transparent red
        } else {
            // Yellow: after 1000ms but below 50% of max (slam-high range)
            barColor = '#FFEB3B';
            barBgColor = 'rgba(255, 235, 59, 0.3)'; // Lighter/transparent yellow
        }
        
        // Helper function to draw rounded rectangle
        const drawRoundedRect = (x, y, width, height, radius) => {
            ctx.beginPath();
            ctx.moveTo(x + radius, y);
            ctx.lineTo(x + width - radius, y);
            ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
            ctx.lineTo(x + width, y + height - radius);
            ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
            ctx.lineTo(x + radius, y + height);
            ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
            ctx.lineTo(x, y + radius);
            ctx.quadraticCurveTo(x, y, x + radius, y);
            ctx.closePath();
        };
        
        // Draw background fill (full width, lighter/transparent version of current color)
        ctx.fillStyle = barBgColor;
        drawRoundedRect(barX, barY, barWidth, barHeight, cornerRadius);
        ctx.fill();
        
        // Draw progress fill (actual width based on aggression, full opacity)
        const fillWidth = barWidth * fillPercent;
        if (fillWidth > 0) {
            ctx.fillStyle = barColor;
            // Draw fill with actual width (no minimum clamp) so bar starts at 0% and fills smoothly
            drawRoundedRect(barX, barY, fillWidth, barHeight, cornerRadius);
            ctx.fill();
        }
        
        // Always draw border (black, rounded rectangle) so bar is visible even at 0 aggression
        ctx.strokeStyle = '#000000';
        ctx.lineWidth = 2; // Slightly thicker for visibility
        drawRoundedRect(barX, barY, barWidth, barHeight, cornerRadius);
        ctx.stroke();
        
        // NOTE: Don't restore camera transform here - it's restored in game.js after all UI is drawn
    }
}
